/**
 * Platform admin — Chat Monitor
 *
 * Left  — filterable feedback list (only conversations that received feedback)
 * Right — selected conversation: the specific user+agent pair returned by the backend,
 *         feedback badge, agent steps toggle, and an Analyze / Copy / Export toolbar.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  useListPlatformChatFeedbackQuery,
  useGetPlatformChatConversationQuery,
  useGetPlatformConversationRunsQuery,
  useListLlmModelsQuery,
  useAnalyzeConversationTurnMutation,
  type ChatFeedbackEntry,
  type PlatformChatMessage,
  type PlatformConversation,
} from '@/redux/internal-services/platformAdminApi';
import { AgentStepsView } from '@/components/chat/AgentStepsView';
import type { AgentRunStep } from '@/redux/internal-services/agentApi';
import { Button } from '@/components/UI/Button';
import { Chip } from '@/components/UI/Chip';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';
import { cn } from '@/utils/cn';

// ── helpers ───────────────────────────────────────────────────────────────────

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function shortId(id?: string | null) {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…${id.slice(-4)}` : id;
}

// ── Platform-aware turn steps ─────────────────────────────────────────────────

const PlatformTurnSteps: React.FC<{ conversationId: string; targetStepId?: string }> = ({
  conversationId, targetStepId,
}) => {
  const [open, setOpen] = useState(false);
  const { data, isFetching, isError } = useGetPlatformConversationRunsQuery(
    { conversationId },
    { skip: !open },
  );
  const runs = (data?.data ?? []) as { run_id?: string; steps?: AgentRunStep[] }[];

  // Build flattened steps with their reconstructed "{run_id}:a:{i}" IDs for target matching
  const stepsWithRecon = runs.flatMap((r) =>
    (r.steps ?? []).map((step, si) => ({ step, reconId: `${r.run_id}:a:${si}` }))
  );
  const steps = stepsWithRecon.map((s) => s.step);
  const targetIndex = targetStepId
    ? stepsWithRecon.findIndex((s) => s.reconId === targetStepId)
    : -1;

  const scrollRef = useRef<HTMLDivElement>(null);
  const targetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || isFetching) return;
    if (targetRef.current) {
      targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } else if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [open, isFetching, targetStepId]);

  return (
    <div className="mt-1 ml-1">
      <button
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-xs text-ui-text3 transition-colors hover:text-ui-text2 focus-ring"
      >
        <span className={cn('transition-transform', open && 'rotate-90')}>›</span>
        {open ? 'Hide agent steps' : 'View agent steps'}
      </button>
      {open && (
        <div ref={scrollRef} className="mt-1.5 max-h-64 overflow-y-auto">
          {isFetching && <p className="py-1 text-xs text-ui-text2">Loading steps…</p>}
          {isError && <p className="py-1 text-xs text-danger-text">Couldn't load steps.</p>}
          {!isFetching && !isError && (
            <AgentStepsView
              steps={steps}
              targetIndex={targetIndex >= 0 ? targetIndex : undefined}
              targetRef={targetRef}
            />
          )}
        </div>
      )}
    </div>
  );
};

// ── Analyze panel ─────────────────────────────────────────────────────────────

const AnalyzePanel: React.FC<{
  conversationId: string;
  messageId: string;
}> = ({ conversationId, messageId }) => {
  const [selectedModel, setSelectedModel] = useState('');
  const [userPrompt, setUserPrompt] = useState('');
  const [result, setResult] = useState<string | null>(null);

  const { data: modelsData, isLoading: modelsLoading } = useListLlmModelsQuery();
  const models = modelsData?.data ?? [];

  const [analyze, { isLoading: analyzing }] = useAnalyzeConversationTurnMutation();

  const handleRun = async () => {
    if (!selectedModel) return;
    setResult(null);
    try {
      const res = await analyze({
        conversationId,
        messageId,
        modelId: selectedModel,
        userPrompt: userPrompt.trim() || null,
      }).unwrap();
      setResult(res.data.analysis);
    } catch {
      setResult('Analysis failed. Check the backend logs.');
    }
  };

  return (
    <div className="shrink-0 space-y-3 border-t border-ui-border bg-ui-card px-4 py-3">
      <p className="eyebrow">Analyze with LLM</p>

      {/* Model selector */}
      <div className="flex items-center gap-2">
        <label className="whitespace-nowrap text-xs text-ui-text3">Model</label>
        {modelsLoading ? (
          <span className="text-xs text-ui-text3">Loading models…</span>
        ) : (
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className={cn(fieldClass({ size: 'xs' }), 'flex-1')}
          >
            <option value="">— Select model —</option>
            {models.map((m) => (
              <option key={m.model_id} value={m.model_id}>{m.name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Optional instructions */}
      <textarea
        value={userPrompt}
        onChange={(e) => setUserPrompt(e.target.value)}
        placeholder="Additional instructions (optional) — e.g. 'Focus on tone' or 'Was the tool usage efficient?'"
        rows={2}
        className={cn(fieldClass({ shape: 'box', size: 'xs' }), 'resize-none')}
      />

      {/* Run button */}
      <Button variant="accent" size="sm" onClick={handleRun} disabled={!selectedModel || analyzing}>
        {analyzing ? 'Analyzing…' : '⚡ Run Analysis'}
      </Button>

      {/* Result */}
      {result !== null && (
        <div className="max-h-64 overflow-y-auto rounded-control border border-ui-border bg-ui-subtle p-3">
          <p className="mb-1.5 text-xs font-semibold text-ui-text2">Analysis Result</p>
          <p className="whitespace-pre-wrap text-xs leading-relaxed text-ui-text">
            {result || '(Model returned no content — try a different model.)'}
          </p>
        </div>
      )}
    </div>
  );
};

// ── Conversation detail panel ─────────────────────────────────────────────────

function stepsToLines(steps: AgentRunStep[]): string[] {
  const lines: string[] = [];
  for (const s of steps) {
    const t = s.step_type;
    const meta = s.metadata ?? {};
    const content = s.content as Record<string, unknown> | string | null;

    if (t === 'user_input') {
      const text = typeof content === 'string' ? content : (content as Record<string, string>)?.text ?? '';
      if (text) lines.push(`  [User Input] ${text}`);

    } else if (t === 'agent_response') {
      const text = typeof content === 'string' ? content : (content as Record<string, string>)?.text ?? '';
      if (text) lines.push(`  [Agent Response] ${text}`);

    } else if (t === 'llm_call') {
      // content = { model, prompt: [{role, content}...] }
      const c = content as Record<string, unknown>;
      const msgs = (c?.prompt ?? meta?.messages ?? []) as Array<Record<string, unknown>>;
      const model = (c?.model ?? '') as string;
      lines.push(`  [LLM Call] model=${model} ${msgs.length} message(s)`);
      for (const m of msgs) {
        const role = (m.role ?? '') as string;
        let text = m.content ?? '';
        if (Array.isArray(text)) text = (text as Array<Record<string, string>>).map((p) => p.text ?? '').join(' ');
        if (text) lines.push(`    [${role}]: ${text}`);
      }

    } else if (t === 'session_update') {
      const c = content as Record<string, unknown>;
      const tools = ((c?.request_params as Record<string, unknown> | undefined)?.tools ?? []) as unknown[];
      const added = ((meta?.tools_added ?? []) as unknown[]).length;
      const removed = ((meta?.tools_removed ?? []) as unknown[]).length;
      lines.push(`  [Session Update] ${tools.length} tool(s) (+${added} -${removed})${meta?.ok === false ? ' FAILED' : ''}`);

    } else if (t === 'tool_call') {
      const name = (meta?.tool_name ?? (content as Record<string, unknown>)?.name ?? 'tool') as string;
      const args = meta?.args ?? (content as Record<string, unknown>)?.args ?? {};
      lines.push(`  [Tool Call] ${name}(${JSON.stringify(args)})`);

    } else if (t === 'tool_output') {
      const name = (meta?.tool_name ?? '') as string;
      const out = typeof content === 'object' ? JSON.stringify(content) : String(content ?? '');
      lines.push(`  [Tool Output] ${name}: ${out}`);

    } else if (t === 'agent_action') {
      const title = (meta?.title ?? meta?.caption ?? '') as string;
      const mod = (meta?.module ?? '') as string;
      lines.push(`  [Agent Action] ${mod}: ${title}`);
    }
  }
  return lines;
}

function buildExportPayload(
  convo: PlatformConversation,
  messages: PlatformChatMessage[],
  feedback: Record<string, unknown>,
  steps: AgentRunStep[],
) {
  return {
    conversation_id: convo.session_id,
    user: convo.user_name || convo.user_id,
    tenant_id: convo.tenant_id,
    agent_id: convo.agent_id,
    messages: messages.map((m) => ({
      role: m.role,
      content: m.content,
      created_at: m.created_at,
      feedback: feedback[m.message_id] ?? null,
    })),
    agent_steps: steps,
  };
}

function buildCopyText(
  convo: PlatformConversation,
  messages: PlatformChatMessage[],
  feedback: Record<string, unknown>,
  steps: AgentRunStep[],
) {
  const lines: string[] = [
    `Conversation: ${convo.session_id}`,
    `User: ${convo.user_name || convo.user_id}`,
    `Tenant: ${convo.tenant_id}`,
    `Agent: ${convo.agent_id}`,
    '',
  ];
  for (const m of messages) {
    lines.push(`[${m.role === 'user' ? 'User' : 'Agent'}] ${fmtDate(m.created_at)}`);
    lines.push(m.content || '(empty)');
    const fb = feedback[m.message_id] as { helpful?: boolean; comment?: string; created_at?: string } | undefined;
    if (fb) {
      lines.push(`Feedback: ${fb.helpful ? '👍 Positive' : '👎 Negative'}${fb.comment ? ` — "${fb.comment}"` : ''}`);
    }
    lines.push('');
  }
  if (steps.length > 0) {
    lines.push('=== Agent Steps ===');
    lines.push(...stepsToLines(steps));
    lines.push('');
  }
  return lines.join('\n');
}

const ConversationDetail: React.FC<{
  conversationId: string;
  feedbackEntry?: ChatFeedbackEntry | null;
}> = ({ conversationId, feedbackEntry }) => {
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [specOpen, setSpecOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const feedbackMsgRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Always load the full conversation — messageId is used only for badge/scroll positioning,
  // not to narrow the message list, so the admin sees full context.
  const { data, isLoading, isError } = useGetPlatformChatConversationQuery({
    conversationId,
  });
  const convo: PlatformConversation | undefined = data?.data;

  const messages: PlatformChatMessage[] = convo?.messages ?? [];
  const assistantMsg = messages.find((m) => m.role === 'assistant');

  // Eagerly fetch all runs so Copy/Export always include the full session steps
  const { data: runsData } = useGetPlatformConversationRunsQuery(
    { conversationId },
    { skip: !conversationId },
  );
  const runs = (runsData?.data ?? []) as { steps?: AgentRunStep[] }[];
  const steps: AgentRunStep[] = runs.flatMap((r) => r.steps ?? []);

  // After messages load, scroll to the message carrying the feedback badge so the admin
  // doesn't have to hunt through a long conversation (especially voice/realtime sessions
  // where the full transcript is returned and the badge is near the bottom).
  // Must be before any conditional return to satisfy Rules of Hooks.
  useEffect(() => {
    if (feedbackMsgRef.current) {
      feedbackMsgRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } else if (scrollContainerRef.current) {
      // No specific message matched (e.g. old voice session) — snap to top so the banner is visible.
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [messages.length, feedbackEntry?.feedback_id]);

  if (isLoading) {
    return <LoadingState label="Loading…" className="h-full" />;
  }
  if (isError || !convo) {
    return <div className="flex h-full items-center justify-center text-sm text-danger-text">Failed to load conversation.</div>;
  }

  const feedback = convo.feedback ?? {};

  // When multiple feedback entries exist in the same session (common for long voice sessions),
  // the backend re-keys all of them into `feedback`. We must show ONLY the selected entry's
  // badge, identified by feedback_id rather than message key.
  type FeedbackDoc = { feedback_id?: string; helpful?: boolean; comment?: string | null; created_at?: string };

  // feedback_id_to_msg: collision-free reverse index from the backend.
  // Maps feedback_id → reconstructed message_id so we can target the exact message even when
  // two feedbacks share the same reconstructed step (timestamp collision in recon_feedback).
  type FeedbackIdToMsg = Record<string, string>;
  const feedbackIdToMsg = ((convo as unknown as Record<string, unknown>).feedback_id_to_msg ?? {}) as FeedbackIdToMsg;

  // The reconstructed message_id that the selected feedback entry belongs to (if known).
  const targetMsgId = feedbackEntry?.feedback_id ? feedbackIdToMsg[feedbackEntry.feedback_id] : undefined;
  const feedbackAttachedToAMessage = !!targetMsgId && messages.some((m) => m.message_id === targetMsgId);

  const handleCopy = () => {
    const text = buildCopyText(convo, messages, feedback, steps);
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {});
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExport = () => {
    const payload = buildExportPayload(convo, messages, feedback, steps);
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-feedback-${conversationId.slice(0, 8)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="shrink-0 border-b border-ui-border p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ui-text truncate">
              {convo.user_name || shortId(convo.user_id)}
            </p>
            {convo.user_email && (
              <p className="text-xs text-ui-text3 mt-0.5 truncate">{convo.user_email}</p>
            )}
            <p className="text-xs font-mono text-ui-text3 mt-0.5 truncate">{convo.session_id}</p>
            <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-ui-text3">
              <span>tenant: <strong className="text-ui-text2">{shortId(convo.tenant_id)}</strong></span>
              <span>agent: <strong className="text-ui-text2">{convo.agent_id ?? '—'}</strong></span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              variant={specOpen ? 'soft' : 'outline'}
              size="xs"
              onClick={() => setSpecOpen((o) => !o)}
              title="Session spec"
              aria-pressed={specOpen}
            >
              ⓘ Info
            </Button>
            <Button
              variant={analyzeOpen ? 'accent' : 'outline'}
              size="xs"
              onClick={() => setAnalyzeOpen((o) => !o)}
              aria-pressed={analyzeOpen}
            >
              ⚡ Analyze
            </Button>
            <Button variant="outline" size="xs" onClick={handleCopy}>
              {copied ? '✓ Copied' : '📋 Copy'}
            </Button>
            <Button variant="outline" size="xs" onClick={handleExport}>
              ⬇ Export
            </Button>
          </div>
        </div>

        {/* Collapsible session spec panel */}
        {specOpen && (
          <div className="mt-3 space-y-3 rounded-control border border-ui-border bg-ui-subtle px-4 py-3 text-xs">
            {/* Profile row */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
              <div>
                <span className="text-ui-text3">User</span>
                <p className="text-ui-text font-medium truncate">{convo.user_name || '—'}</p>
              </div>
              <div>
                <span className="text-ui-text3">Email</span>
                <p className="text-ui-text truncate">{convo.user_email || feedbackEntry?.user_email || '—'}</p>
              </div>
              <div>
                <span className="text-ui-text3">Agent</span>
                <p className="text-ui-text font-mono truncate">{convo.agent_id || '—'}</p>
              </div>
              <div>
                <span className="text-ui-text3">Total messages</span>
                <p className="text-ui-text">{convo.total_messages ?? '—'}</p>
              </div>
              <div>
                <span className="text-ui-text3">Session started</span>
                <p className="text-ui-text">{fmtDate(convo.created_at)}</p>
              </div>
              <div>
                <span className="text-ui-text3">Last activity</span>
                <p className="text-ui-text">{fmtDate(convo.last_activity_at)}</p>
              </div>
              {feedbackEntry && (
                <>
                  <div>
                    <span className="text-ui-text3">Feedback</span>
                    <p className="text-ui-text font-medium">{feedbackEntry.helpful ? '👍 Positive' : '👎 Negative'}</p>
                  </div>
                  <div>
                    <span className="text-ui-text3">Submitted</span>
                    <p className="text-ui-text">{fmtDate(feedbackEntry.created_at)}</p>
                  </div>
                  {feedbackEntry.comment && (
                    <div className="col-span-2">
                      <span className="text-ui-text3">Comment</span>
                      <p className="text-ui-text mt-0.5 italic">"{feedbackEntry.comment}"</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* DB refs — click any row to copy the full ID */}
            <div className="space-y-1 border-t border-ui-border pt-2.5">
              <p className="eyebrow mb-1.5">DB refs (click to copy)</p>
              {(
                [
                  { label: 'session_id', value: convo.session_id, hint: 'sessions / conversation_runs' },
                  { label: 'user_id',    value: convo.user_id,    hint: 'users' },
                  { label: 'tenant_id',  value: convo.tenant_id,  hint: 'tenants' },
                  ...(feedbackEntry ? [
                    { label: 'feedback_id', value: feedbackEntry.feedback_id, hint: 'message_feedback' },
                    { label: 'message_id',  value: feedbackEntry.message_id,  hint: 'message_feedback / run step key' },
                  ] : []),
                  ...(assistantMsg ? [
                    { label: 'run_id', value: assistantMsg.message_id.split(':')[0], hint: 'conversation_runs' },
                  ] : []),
                ] as { label: string; value: string | null | undefined; hint: string }[]
              ).map(({ label, value, hint }) => (
                <button
                  key={label}
                  title={`${hint} — click to copy`}
                  disabled={!value}
                  onClick={() => value && navigator.clipboard?.writeText(value).catch(() => {})}
                  className="flex w-full items-center gap-2 rounded-chip px-2 py-1 text-left transition-colors hover:bg-ui-hover focus-ring disabled:cursor-default disabled:opacity-40"
                >
                  <span className="w-24 shrink-0 text-ui-text3">{label}</span>
                  <span className="flex-1 truncate font-mono text-ui-text">{value || '—'}</span>
                  <span className="shrink-0 text-ui-text3 opacity-50">⎘</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Message list */}
      <div ref={scrollContainerRef} className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4">
        {/* Banner for voice sessions where exact message can't be pinpointed */}
        {!feedbackAttachedToAMessage && feedbackEntry && (
          <div className="flex items-start gap-2 rounded-control border border-warning/40 bg-warning/10 px-3 py-2.5">
            <span className="shrink-0 text-base">{feedbackEntry.helpful ? '👍' : '👎'}</span>
            <div className="min-w-0">
              <span className="text-xs font-medium text-warning-text">
                {feedbackEntry.helpful ? 'Positive feedback' : 'Negative feedback'}
                <span className="text-xs text-ui-text3 ml-2">· {fmtDate(feedbackEntry.created_at)}</span>
              </span>
              {feedbackEntry.comment && (
                <p className="text-xs text-ui-text2 mt-0.5">{feedbackEntry.comment}</p>
              )}
              <p className="text-xs text-ui-text3 mt-1 italic">Specific message not identified — showing full conversation.</p>
            </div>
          </div>
        )}
        {messages.length === 0 && (
          <EmptyState variant="inline" title="Message not found in conversation." />
        )}
        {messages.map((msg) => {
          const isUser = msg.role === 'user';
          // Show the badge only on the exact message the selected feedback targets.
          // Use feedbackEntry directly (not rawFb) so collisions in recon_feedback don't matter.
          const msgFeedback: FeedbackDoc | undefined =
            msg.message_id === targetMsgId && feedbackEntry
              ? { helpful: feedbackEntry.helpful, comment: feedbackEntry.comment, created_at: feedbackEntry.created_at }
              : undefined;

          return (
            <div
              key={msg.message_id}
              ref={msg.message_id === targetMsgId ? feedbackMsgRef : undefined}
              className={cn('flex flex-col', isUser ? 'items-end' : 'items-start')}
            >
              <div
                className={cn(
                  'max-w-[80%] rounded-card px-3.5 py-2 text-sm leading-relaxed',
                  isUser
                    ? 'bg-accent-fill text-on-accent'
                    : msgFeedback
                    ? 'border border-warning bg-ui-subtle text-ui-text ring-1 ring-warning/50'
                    : 'border border-ui-border bg-ui-subtle text-ui-text',
                )}
              >
                {msg.content || <span className="italic text-xs opacity-60">(empty)</span>}
              </div>

              {msg.created_at && (
                <span className="mt-0.5 px-1 text-xs text-ui-text3">{fmtDate(msg.created_at)}</span>
              )}

              {!isUser && msgFeedback && (
                <div className="ml-1 mt-1.5 flex max-w-[80%] items-start gap-1.5 rounded-chip border border-warning/40 bg-warning/10 px-2.5 py-1.5">
                  <span className="shrink-0 text-base">{msgFeedback.helpful ? '👍' : '👎'}</span>
                  <div className="flex min-w-0 flex-col">
                    <span className="text-xs font-medium text-warning-text">
                      {msgFeedback.helpful ? 'Positive feedback' : 'Negative feedback'}
                    </span>
                    {msgFeedback.comment && (
                      <span className="text-xs text-ui-text2 mt-0.5">{msgFeedback.comment}</span>
                    )}
                    <span className="text-xs text-ui-text3 mt-0.5">{fmtDate(msgFeedback.created_at)}</span>
                  </div>
                </div>
              )}

              {!isUser && (
                <PlatformTurnSteps
                  conversationId={conversationId}
                  targetStepId={msg.message_id === targetMsgId ? targetMsgId : undefined}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Analyze panel — shown below messages when toggled */}
      {analyzeOpen && assistantMsg && (
        <AnalyzePanel
          conversationId={conversationId}
          messageId={assistantMsg.message_id}
        />
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const ChatMonitorPage: React.FC = () => {
  const [filterHelpful, setFilterHelpful] = useState<boolean | undefined>(undefined);
  const [offset, setOffset] = useState(0);
  const [selectedFeedback, setSelectedFeedback] = useState<ChatFeedbackEntry | null>(null);
  const limit = 50;

  const { data, isLoading } = useListPlatformChatFeedbackQuery({
    helpful: filterHelpful,
    limit,
    offset,
  });

  const entries: ChatFeedbackEntry[] = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="flex h-full min-h-0">
      {/* ── Left: feedback list ── */}
      <div className="flex w-80 shrink-0 flex-col border-r border-ui-border bg-ui-card">
        <div className="space-y-2 border-b border-ui-border p-3">
          <h1 className="text-h2 text-ui-text">Chat Feedback</h1>
          <div className="flex gap-1.5">
            {(
              [
                { label: 'All', value: undefined },
                { label: '👍 Positive', value: true },
                { label: '👎 Negative', value: false },
              ] as { label: string; value: boolean | undefined }[]
            ).map(({ label, value }) => (
              <Chip
                key={label}
                selected={filterHelpful === value}
                onClick={() => { setFilterHelpful(value); setOffset(0); }}
              >
                {label}
              </Chip>
            ))}
          </div>
          <p className="text-xs text-ui-text3">{total} feedback entries</p>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && <LoadingState label="Loading…" className="py-8" />}
          {!isLoading && entries.length === 0 && (
            <EmptyState title="No feedback found." className="py-8" />
          )}
          {entries.map((entry) => (
            <button
              key={entry.feedback_id}
              onClick={() => setSelectedFeedback(entry)}
              className={cn(
                'w-full border-b border-ui-border px-3 py-2.5 text-left transition-colors hover:bg-ui-hover focus-ring',
                selectedFeedback?.feedback_id === entry.feedback_id && 'border-l-2 border-l-accent bg-accent/10',
              )}
            >
              <div className="flex items-center gap-1.5">
                <span className="text-base">{entry.helpful ? '👍' : '👎'}</span>
                <p className="flex-1 truncate text-xs font-medium text-ui-text">
                  {entry.user_name || shortId(entry.user_id)}
                </p>
                <span className="shrink-0 text-xs text-ui-text3">
                  {fmtDate(entry.created_at).split(',')[0]}
                </span>
              </div>
              <p className="text-xs text-ui-text2 truncate mt-0.5">
                {entry.session_title || shortId(entry.session_id)}
              </p>
              {entry.comment && (
                <p className="text-xs text-ui-text3 truncate italic mt-0.5">"{entry.comment}"</p>
              )}
            </button>
          ))}
        </div>

        {total > limit && (
          <div className="flex items-center justify-between border-t border-ui-border p-2 text-xs text-ui-text2">
            <Button
              variant="ghost"
              size="xs"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - limit))}
            >
              ← Prev
            </Button>
            <span>{offset + 1}–{Math.min(offset + limit, total)} of {total}</span>
            <Button
              variant="ghost"
              size="xs"
              disabled={offset + limit >= total}
              onClick={() => setOffset((o) => o + limit)}
            >
              Next →
            </Button>
          </div>
        )}
      </div>

      {/* ── Right: conversation detail ── */}
      <div className="min-w-0 flex-1 bg-ui">
        {selectedFeedback ? (
          <ConversationDetail
            conversationId={selectedFeedback.session_id}
            feedbackEntry={selectedFeedback}
          />
        ) : (
          <EmptyState title="Select a feedback entry to view the conversation." className="h-full" />
        )}
      </div>
    </div>
  );
};

export default ChatMonitorPage;
